import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { ParamReader } from './ParamReader';
import { sharedSpanListItems } from './spans';

/** blow 372's stat enum, from the wiki's "## 372 Convert Stat" table. */
const STATS_372 = [
    'STR', 'AGI', 'INT', 'VIT', 'HP', 'PATK', 'MATK', 'PDEF', 'MDEF', 'CRIT', 'FD', 'MP', 'MvtSpd', 'CDMG',
];

/** 1-based fire/ice/light/dark enum shared by blows 390/406 (unlike 182, which is 0-based). */
const ELEMENTS_1_BASED_ALL = ['(all)', 'Fire', 'Ice', 'Light', 'Dark'];
const ELEMENTS_1_BASED_NONE = ['(none)', 'Fire', 'Ice', 'Light', 'Dark'];

export const misc: Record<number, IBlowDef> = {
    26: {
        name: 'Down delay',
        wikiDesc: 'Shortens your own knockdown lie time: down time = 3s × (1 - Mod) (0.5 = get up ' +
            'twice as fast, negative = longer). Stacks additively.',
        describe({ params }) {
            const modValue = params.nextNum();
            if (isNaN(modValue)) {
                return null;
            }
            const dir = modValue >= 0 ? 'reduced' : 'increased';
            return [
                `Own knockdown lie time ${dir} by `, seg.pct(Math.abs(modValue)),
                ' (3s base, stacks additively)',
            ];
        },
    },
    40: {
        name: 'Concentration',
        status: 'unused',
        wikiDesc: 'Has no effect.',
    },
    48: {
        name: 'Skill Points',
        status: 'unused',
        wikiDesc: 'No effect (increases SP available on official servers). Parameter is AdditionalSP.',
    },
    49: {
        name: 'Add Experience',
        wikiDesc: 'Adds leveling EXP.',
        describe({ params }) {
            const amount = params.nextNum();
            if (isNaN(amount)) {
                return null;
            }
            return ['Grants ', seg.num(amount), ' additional EXP'];
        },
    },
    50: {
        name: 'Increase Damage',
        wikiDesc: 'True damage. Specify a layer ID to stack additively within that layer; layers are ' +
            'multiplicative with each other. Optionally restrict to a comma list of skill IDs so the ' +
            'bonus only applies to hits from those skills.',
        note: 'Known layer IDs: 0 skill plates, 1 unknown, 2 baked-in damage (Pingu?), 3 Solace of ' +
            'Oblivion, 4 Blessing of Strikes, 99 DynBal.',
        describe({ params }) {
            const f = params.fields();
            const ratio = ParamReader.num(f[0]);
            if (isNaN(ratio)) {
                return null;
            }
            const out: Segment[] = ['True damage increased by ', seg.pct(ratio)];
            const layerId = f.length > 1 ? ParamReader.num(f[1]) : NaN;
            if (!isNaN(layerId)) {
                out.push(' (layer ', seg.id(layerId), ')');
            }
            const skillIds = f.length > 2 ? ParamReader.numList(f[2], ',') : [];
            if (skillIds.length) {
                out.push(', restricted to ',
                    seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])));
            }
            return out;
        },
    },
    51: {
        name: 'Transmit Damage',
        wikiDesc: 'On a summon: all damage it takes is redirected to its owner instead. Can kill the owner.',
        note: 'Value parameter has no effect.',
        describe() {
            return ['All damage this summon takes is redirected to its owner instead (can kill the owner)'];
        },
    },
    56: {
        name: 'Old Parry',
        wikiDesc: 'Gives Chance% chance to parry incoming attacks for the duration.',
        describe({ params }) {
            const chance = params.nextNum();
            if (isNaN(chance)) {
                return null;
            }
            return ['Gives a ', seg.pct(chance), ' chance to parry incoming attacks'];
        },
    },
    57: {
        name: 'Revive',
        wikiDesc: 'Revives the given actor, either unconditionally on stage clear, or with explicit ' +
            'HP%/MP%/invincibility-time parameters.',
        describe({ raw }) {
            const trimmed = raw.trim();
            if (trimmed === '') {
                return null;
            }
            if (trimmed.toLowerCase() === 'stageclear') {
                return ['Revives the actor when the stage is cleared'];
            }
            const parts = trimmed.split('/');
            if (parts.length < 4) {
                return null;
            }
            const hpPct = Number(parts[1]);
            const mpPct = Number(parts[2]);
            const invincibleSec = Number(parts[3]);
            if (isNaN(hpPct) || isNaN(mpPct) || isNaN(invincibleSec)) {
                return null;
            }
            const out: Segment[] = [
                'Revives the actor with ', seg.pct(hpPct / 100), ' HP and ', seg.pct(mpPct / 100), ' MP',
            ];
            if (invincibleSec) {
                out.push(', granting ', seg.secs(invincibleSec * 1000), ' of invincibility');
            }
            return out;
        },
    },
    60: {
        name: 'Chained Attack',
        wikiDesc: 'Chain Lightning effect: bounces between enemies.',
        describe({ params }) {
            const f = params.numbers();
            const range = f[0];
            const bounces = f[1];
            const rate = f[2];
            if (isNaN(range) || isNaN(bounces) || isNaN(rate)) {
                return null;
            }
            return [
                'Chain lightning bounces up to ', seg.num(bounces), ' times between enemies within ',
                seg.num(range), ' range at a ', seg.pct(rate), ' chance per bounce',
            ];
        },
    },
    62: {
        name: 'Apply layered action',
        wikiDesc: 'Applies a layered action effect using the given action (also seems to change ' +
            'unskilled actions to be childed to the applying skill).',
        describe({ raw }) {
            const action = raw.trim();
            if (!action) {
                return null;
            }
            return ['Applies the layered action ', seg.action(action)];
        },
    },
    66: {
        name: 'Add Aggro',
        wikiDesc: 'Adds aggro to any actor in a cone in front of the caster, spanning +/- angle/2 with ' +
            'the given range.',
        describe({ params }) {
            const f = params.numbers();
            const aggro = f[0];
            const range = f[1];
            const angle = f[2];
            if (isNaN(aggro) || isNaN(range) || isNaN(angle)) {
                return null;
            }
            return [
                'Adds ', seg.num(aggro), ' aggro to enemies within ', seg.num(range),
                ' range in a ', seg.num(angle), '° cone in front',
            ];
        },
    },
    69: {
        name: 'Cleanse',
        wikiDesc: 'Removes Count detrimental effects.',
        describe({ params }) {
            const f = params.numbers();
            const count = f[0];
            if (isNaN(count)) {
                return null;
            }
            const out: Segment[] = ['Removes ', seg.num(count), ` detrimental effect${count === 1 ? '' : 's'}`];
            const flags: string[] = [];
            if (f[1] === 1) {
                flags.push('ignoring team check');
            }
            if (f[2] === 1) {
                flags.push('ignoring dissolvable flag');
            }
            if (flags.length) {
                out.push(' (', flags.join(', '), ')');
            }
            return out;
        },
    },
    72: {
        name: 'Mental Charge',
        wikiDesc: 'Required by SkillUsableChecker 8 (Energy Charge); used by SkillProcessor 4 (Impact ' +
            'Blow) to change the attack type.',
        describe({ params }) {
            const f = params.numbers();
            if (!f.length || isNaN(f[0])) {
                return null;
            }
            let attackType = 0;
            let value = f[0];
            if (f.length >= 2 && !isNaN(f[1])) {
                attackType = f[0];
                value = f[1];
            }
            return [
                'Energy charge (', seg.en(attackType, ['physical', 'magical'], 'unknown'), ' type), value ',
                seg.num(value),
            ];
        },
    },
    74: {
        name: 'Maximum Damage',
        wikiDesc: 'Uses the upper end of the damage range instead of randomly picking; final damage ' +
            'modifiers still apply.',
    },
    79: {
        name: 'Forcibly destroy super armor',
        wikiDesc: 'Attacks always break super armor.',
    },
    82: {
        name: 'FD%',
        status: 'deprecated',
        wikiDesc: 'Old and unused — use 389 instead.',
    },
    83: {
        name: 'Buff Wipe',
        wikiDesc: 'Removes the given number of buffs.',
        describe({ params }) {
            const count = params.nextNum();
            if (isNaN(count)) {
                return null;
            }
            return ['Removes ', seg.num(count), count === 1 ? ' buff' : ' buffs'];
        },
    },
    100: {
        name: 'Attach Visual Effect',
        wikiDesc: 'Cosmetic only. Attaches a state-effect-table VFX to the actor, plays a common-effect ' +
            "action, or replays the signals of one of the caster's actions at the target's position. 376 " +
            'is a clean copy of this blow.',
        describe({ raw }) {
            const trimmed = raw.trim();
            if (trimmed === '') {
                return null;
            }
            if (trimmed.indexOf(';') === -1) {
                const id = Number(trimmed);
                if (isNaN(id)) {
                    return null;
                }
                return ['Attaches state-effect VFX table ', seg.id(id)];
            }
            const parts = trimmed.split(';');
            const type = Number(parts[0]);
            const actionName = parts.slice(1).join(';');
            if (isNaN(type) || !actionName) {
                return null;
            }
            const TYPES = ['(none)', 'common effect', 'signal effect'];
            return ['Plays a ', seg.en(type, TYPES, 'unknown'), ' action ', seg.action(actionName)];
        },
    },
    121: {
        name: 'Change Stand action name',
        wikiDesc: 'Stance: whenever the actor would enter Stand, the given action plays instead. ' +
            '<Prefix>_Start/_End variants are looked up automatically and played on begin/end. While ' +
            "active, hits count as skill hits (not basic attacks) and inherit the parent skill's element.",
        describe({ raw }) {
            const action = raw.trim();
            if (!action) {
                return null;
            }
            return ['Replaces Stand with the action ', seg.action(action)];
        },
    },
    129: {
        name: 'Use alternative action name',
        wikiDesc: "Enables the parent skill's CHANGE_ACTIONSTR processor so action names get substituted " +
            'in SetActionQueue (EX stance attack replacement). Disabled if the skill weapon requirement ' +
            'is not met. Hits are not treated as basic attacks while applied.',
        describe({ raw }) {
            const action = raw.trim();
            if (!action || action === '0') {
                return ['Enables action-name substitution for the skill (active at all times)'];
            }
            return [
                'Enables action-name substitution for the skill, active only while performing ',
                seg.action(action),
            ];
        },
    },
    132: {
        name: 'Generate threat',
        wikiDesc: 'Increases threat by Threat.',
        describe({ params }) {
            const threat = params.nextNum();
            if (isNaN(threat)) {
                return null;
            }
            return ['Increases threat by ', seg.num(threat)];
        },
    },
    137: {
        name: 'Death defied',
        wikiDesc: '(Monster only) Upon reaching 0 HP, delay death: HP is set to 1, AI disabled, ' +
            'invincibility applied, then it casts SkillID and dies when the skill finishes. Ends ' +
            "immediately if the monster doesn't own the skill.",
        describe({ params }) {
            const skillId = params.nextNum();
            if (isNaN(skillId)) {
                return null;
            }
            return ['On reaching 0 HP, delays death to cast ', seg.skill(skillId), ' first'];
        },
    },
    148: {
        name: 'Reset aggro',
        wikiDesc: 'Resets aggro (monster targets only).',
        describe({ params }) {
            const type = params.nextNum();
            if (isNaN(type)) {
                return null;
            }
            const TYPES = ['hard reset at blow start and end', 'no aggro for the duration (restored after)'];
            return ['Resets aggro: ', seg.en(type, TYPES, 'unknown')];
        },
    },
    264: {
        name: 'Fishing Boost',
        wikiDesc: 'Decreases the time it takes to fish by Mod seconds.',
    },
    345: {
        name: 'Enhance skills',
        wikiDesc: 'Enhances each listed skill into its paired replacement skill, showing a glow on ' +
            'the skill bar (color and optional per-pair use-count configurable).',
        // Custom parse: pair list with per-pair `,HEXCODE,UseCount` and optional `|HEXCODE` tail.
        // Rendered as a two-column table (source → replacement); deliberately unspanned.
        describe({ raw }) {
            let body = raw;
            let globalColor: string | null = null;
            const bar = raw.lastIndexOf('|');
            if (bar >= 0) {
                body = raw.slice(0, bar);
                const tail = raw.slice(bar + 1).trim();
                globalColor = tail === '' ? null : tail;
            }
            const f = body.split(';').map((s) => s.trim());
            if (f.length < 2) {
                return null;
            }
            const rows: Segment[][][] = [];
            for (let i = 0; i + 1 < f.length; i += 2) {
                const fromId = ParamReader.num(f[i]);
                const toField = f[i + 1];
                const toParts = toField.split(',');
                const toId = ParamReader.num(toParts[0]);
                if (isNaN(fromId) || isNaN(toId)) {
                    continue;
                }
                const toCell: Segment[] = ['→ ', seg.skill(toId)];
                const pairColor = (toParts[1] || '').trim();
                const color = pairColor !== '' ? pairColor : globalColor;
                if (color === '0') {
                    toCell.push(' (no glow)');
                } else if (color) {
                    toCell.push(' (glow #' + color + ')');
                }
                const useCount = ParamReader.num(toParts[2]);
                if (!isNaN(useCount) && useCount > 0) {
                    toCell.push(` (${useCount} uses)`);
                }
                rows.push([[seg.skill(fromId)], toCell]);
            }
            if (!rows.length) {
                return null;
            }
            return ['Enhances:', seg.table(rows)];
        },
    },
    151: {
        name: 'Camera distortion effect',
        wikiDesc: 'Drunk/shaky screen camera: full-screen motion blur on the local player for the ' +
            'duration (client visual only).',
        describe({ params }) {
            const blend = params.nextNum();
            const out: Segment[] = ['Applies a shaky-camera motion blur to the local player (client only)'];
            if (!isNaN(blend)) {
                out.push(' at blend factor ', seg.num(blend));
            }
            return out;
        },
    },
    152: {
        name: 'Camera turn speed reduction effect',
        wikiDesc: "Multiplies the local player's camera turn speed by Ratio; movement unaffected " +
            '(client only).',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            if (ratio === 0) {
                return ["Locks the local player's camera turn speed"];
            }
            return ["Multiplies the local player's camera turn speed by ", seg.pct(ratio)];
        },
    },
    155: {
        name: 'Dissolve State',
        wikiDesc: 'Removes the listed state effects if the player level is between MinLevel and ' +
            'MaxLevel. For removing specific skill effects, use 211 instead.',
        describe({ params }) {
            const g = params.groups();
            if (!g.groups.length) {
                return null;
            }
            const ids = ParamReader.numList(g.groups[0], ';');
            if (!ids.length) {
                return null;
            }
            const out: Segment[] = ['Removes ', seg.list(ids.map((id) => [seg.blow(id)] as Segment[]))];
            if (g.groups.length > 1) {
                const range = new ParamReader(g.groups[1]).numbers();
                const min = range[0];
                const max = range[1];
                if (!isNaN(min) && !isNaN(max)) {
                    out.push(' if the player is level ', seg.num(min), '–', seg.num(max));
                }
            }
            return out;
        },
    },
    156: {
        name: 'Infinite super armor',
        wikiDesc: 'Super armor damage calculation is skipped entirely, so SA can never be depleted. ' +
            'Blow 314 still breaks through it.',
        describe() {
            return ["Super armor can never be depleted (", seg.blow(314), ' still breaks through)'];
        },
    },
    158: {
        name: 'Duplicate hit damage',
        wikiDesc: 'Every hit taken while active is applied a second time for the same damage (can kill).',
        describe() {
            return ['Every hit taken is applied a second time for the same damage (can kill)'];
        },
    },
    167: {
        name: 'Set summon skill level',
        wikiDesc: "Sets the summoned monster's skill levels to SkillLevel.",
        describe({ params }) {
            const level = params.nextNum();
            if (isNaN(level)) {
                return null;
            }
            return ["Sets the summon's skills to level ", seg.num(level)];
        },
    },
    174: {
        name: 'Buff steal',
        wikiDesc: 'Copies up to Count buff-type effects from one random enemy within ScanRange onto ' +
            'the caster for NewDuration. Not an actual steal — the victim keeps their buffs; re-stealing ' +
            "replaces the caster's previously copied ones.",
        describe({ params }) {
            const f = params.numbers();
            const count = f[0];
            const newDurationMs = f[1];
            const scanRange = f[2];
            if (isNaN(count) || isNaN(newDurationMs) || isNaN(scanRange)) {
                return null;
            }
            return [
                'Copies up to ', seg.num(count), ' buffs from a random enemy within ', seg.num(scanRange),
                ' range for ', seg.secs(newDurationMs),
            ];
        },
    },
    176: {
        name: 'Allowed Skills',
        wikiDesc: 'Only the listed skill IDs can be used; everything else is rejected (ghoul mode).',
        describe({ params }) {
            const ids = params.numbers().filter((v) => !isNaN(v));
            if (!ids.length) {
                return null;
            }
            return ['Restricts skill use to ', seg.list(ids.map((id) => [seg.skill(id)] as Segment[]))];
        },
    },
    177: {
        name: 'Damage',
        status: 'internal',
        wikiDesc: 'Program-internal one-shot: deals AddDamage flat damage (shown as a floating number) ' +
            'on apply. Other blows (162, 292, 293, 294, 299, etc.) spawn this for their bonus damage — ' +
            'not meant to be placed in data by hand.',
        describe({ params }) {
            const f = params.numbers();
            const amount = f[0];
            if (isNaN(amount)) {
                return null;
            }
            const out: Segment[] = ['Deals ', seg.num(amount), ' flat damage'];
            if (f[1] === 1) {
                out.push(" (tagged with the parent skill's ID)");
            }
            return out;
        },
    },
    179: {
        name: 'On hit action',
        wikiDesc: 'Upon striking a target, action changes to Action1, then Action2, then ...',
        describe({ params }) {
            const actions = params.fields(';').filter((a) => a !== '');
            if (!actions.length) {
                return null;
            }
            return ['Upon striking a target, changes action to ',
                seg.list(actions.map((a) => [seg.action(a)] as Segment[]))];
        },
    },
    180: {
        name: 'Magical Breeze',
        wikiDesc: 'Changes the damage type of all attacks cast by the user to magical damage; PATK is ' +
            'folded into MATK and physical stat blows are remapped to their magical counterparts while ' +
            'active.',
        describe() {
            return ['All attacks are converted to magical damage (PATK folds into MATK, physical stat ' +
                'blows remap to their magical counterparts)'];
        },
    },
    181: {
        name: 'Holy Water',
        wikiDesc: 'Force-untransforms the target from ghoul (zombie PvP mode only; credits the ' +
            'thrower a kill).',
        describe() {
            return ['Force-untransforms the target from ghoul (zombie PvP mode only; credits the ' +
                'thrower a kill)'];
        },
    },
    182: {
        name: 'Change Element',
        wikiDesc: 'Hits that have no element get their element set to ElementID; already-elemental ' +
            'hits are untouched. Only the first applied instance counts (weapon prefix effect).',
        describe({ params }) {
            const id = params.nextNum();
            if (isNaN(id)) {
                return null;
            }
            const ELEMENTS = ['Fire', 'Ice', 'Light', 'Dark'];
            return ['Sets unelemental hits to ', seg.en(id, ELEMENTS, 'unknown'), ' element'];
        },
    },
    205: {
        name: 'Teleport_Object',
        wikiDesc: 'Teleports the actor a certain distance using a projectile/offset grammar (sometimes ' +
            'needs an object ID, sometimes only a distance).',
        describe({ raw }) {
            const f = raw.split(';').map((s) => s.trim());
            if (f.length < 5) {
                return null;
            }
            const projectileId = ParamReader.num(f[0]);
            const offsets = ParamReader.numList(f[1], ',');
            const actions = f[2].split(',').map((s) => s.trim());
            const delayMs = ParamReader.num(f[3]);
            const maxDistance = ParamReader.num(f[4]);
            if (isNaN(projectileId) || offsets.length < 3 || actions.length < 2 || !actions[0] ||
                !actions[1] || isNaN(delayMs) || isNaN(maxDistance)) {
                return null;
            }
            return [
                'Teleports the actor up to ', seg.num(maxDistance), ' using projectile ',
                seg.id(projectileId), ' with offset (', seg.num(offsets[0]), ', ', seg.num(offsets[1]),
                ', ', seg.num(offsets[2]), '), playing ', seg.action(actions[0]), ' on success or ',
                seg.action(actions[1]), ' on failure, after a ', seg.secs(delayMs), ' delay',
            ];
        },
    },
    207: {
        name: 'DamageTransition',
        wikiDesc: 'While on an ally, a Ratio of the damage they take is redirected to the caster instead.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return [
                'While active on an ally, redirects ', seg.pct(ratio),
                ' of the damage they take to the caster instead',
            ];
        },
    },
    208: {
        name: 'Bouncing attack',
        wikiDesc: 'Bounces the attack (SuperBall).',
        describe({ params }) {
            const f = params.numbers();
            const bounces = f[0];
            const range = f[1];
            if (isNaN(bounces) || isNaN(range)) {
                return null;
            }
            const out: Segment[] = [
                'Bounces up to ', seg.num(bounces), ' times within ', seg.num(range), ' range',
            ];
            const bonusPerUnused = f[2];
            if (bonusPerUnused) {
                out.push(', dealing ', seg.num(bonusPerUnused), ' additional damage per unused bounce');
            }
            return out;
        },
    },
    210: {
        name: 'Guild War Radar',
        wikiDesc: 'Shows enemy positions on the guild war minimap while held.',
        describe() {
            return ['Shows enemy positions on the guild war minimap while held'];
        },
    },
    211: {
        name: 'Remove status effect',
        wikiDesc: 'Removes status effects applied by the listed skill IDs.',
        describe({ params }) {
            const ids = params.numbers().filter((v) => !isNaN(v));
            if (!ids.length) {
                return null;
            }
            return [
                'Removes status effects applied by ',
                seg.list(ids.map((id) => [seg.skill(id)] as Segment[])),
            ];
        },
    },
    215: {
        name: 'Order My Summoned Monster',
        wikiDesc: 'Works with the OrderMySummonedMonster signal to tell a summoned monster to use a ' +
            'specific skill.',
        describe({ params }) {
            const skillId = params.nextNum();
            if (isNaN(skillId)) {
                return null;
            }
            return ['Orders the summon to use ', seg.skill(skillId)];
        },
    },
    216: {
        name: 'Recall Summon',
        wikiDesc: 'Teleports summons with the listed monster IDs back to the caster (positions from the ' +
            'PositionSetRecallMonster action signal, up to 4 offsets) and resets their aggro.',
        describe({ params }) {
            const ids = params.numbers().filter((v) => !isNaN(v));
            if (!ids.length) {
                return null;
            }
            return [
                'Recalls summoned monsters ', seg.list(ids.map((id) => [seg.monster(id)] as Segment[])),
                ' back to the caster and resets their aggro',
            ];
        },
    },
    217: {
        name: 'Ghoul Transform',
        wikiDesc: 'Transforms the actor into a ghoul, using one of the listed monster IDs.',
        describe({ params }) {
            const ids = params.numbers().filter((v) => !isNaN(v));
            if (!ids.length) {
                return null;
            }
            return ['Transforms into a ghoul as ', seg.list(ids.map((id) => [seg.monster(id)] as Segment[]))];
        },
    },
    219: {
        name: 'Unstable Holy Water',
        wikiDesc: 'Ghoul-mode Holy Water potion (force untransform).',
        describe({ params }) {
            const f = params.numbers();
            const rate = f[0];
            if (isNaN(rate)) {
                return null;
            }
            const ids = f.slice(1).filter((v) => !isNaN(v));
            const out: Segment[] = [
                'At a ', seg.pct(rate / 100), ' chance, force-untransforms the target from ghoul',
            ];
            if (ids.length) {
                out.push(' (', seg.list(ids.map((id) => [seg.monster(id)] as Segment[])), ')');
            }
            return out;
        },
    },
    226: {
        name: 'Prob Invincible At',
        wikiDesc: "Used by Twilight Wellsprings. Grants a %chance immunity to a specific monster's " +
            'skill, via a nested MonsterID;[Skill][Rate%][Skill2]...;MonsterID2;... grammar.',
    },
    232: {
        name: 'Transform into monster',
        wikiDesc: 'Transforms into monster MonsterID, with end action EndAction and start action ' +
            "StartAction. ReplaceSB replaces the skill bar with the monster's SkillTable (defaults to 1).",
        describe({ params }) {
            const f = params.fields();
            const monsterId = ParamReader.num(f[0]);
            if (isNaN(monsterId)) {
                return null;
            }
            const out: Segment[] = ['Transforms into ', seg.monster(monsterId, params.span(0) || undefined)];
            const startAction = f[7];
            const endAction = f[1];
            if (startAction) {
                out.push(', starting with ', seg.action(startAction, params.span(7) || undefined));
            }
            if (endAction) {
                out.push(', ending with ', seg.action(endAction, params.span(1) || undefined));
            }
            const replaceSB = f[5] === '' || f[5] == null ? 1 : ParamReader.num(f[5]);
            out.push(replaceSB === 0
                ? ", keeping the player's own skill bar"
                : ", replacing the skill bar with the monster's skills");
            const flags: string[] = [];
            if (f[8] === '1') {
                flags.push('keeps bubbles');
            }
            if (f[9] === '1') {
                flags.push('allows CDR');
            }
            if (f[3] === '1') {
                flags.push("uses the player's movement speed");
            }
            if (flags.length) {
                out.push(' (', flags.join(', '), ')');
            }
            const skillDependency = ParamReader.num(f[2]);
            const limitStage = ParamReader.num(f[4]);
            const seTableId = ParamReader.num(f[6]);
            const extras: string[] = [];
            if (!isNaN(skillDependency) && skillDependency) {
                extras.push(`skill dependency ${skillDependency}`);
            }
            if (!isNaN(limitStage) && limitStage) {
                extras.push(`limit stage ${limitStage}`);
            }
            if (!isNaN(seTableId) && seTableId) {
                extras.push(`state-effect table ${seTableId}`);
            }
            if (extras.length) {
                out.push(' [', extras.join(', '), ']');
            }
            return out;
        },
    },
    247: {
        name: 'Puppet',
        wikiDesc: "Requires Stigma (246) on the target. Summons puppet MonsterID on the caster's team " +
            "next to the target for the duration; the puppet's incoming damage is scaled by DamageRate " +
            '(via 134/135). Ends early if target and puppet drift beyond LimitRange; the puppet dies ' +
            'when it ends.',
        describe({ params }) {
            const f = params.fields();
            const monsterId = ParamReader.num(f[0]);
            const damageRate = ParamReader.num(f[1]);
            if (isNaN(monsterId) || isNaN(damageRate)) {
                return null;
            }
            const out: Segment[] = [
                'Requires ', seg.blow(246), " on the target. Summons puppet ", seg.monster(monsterId),
                " on the caster's team next to the target, scaling its incoming damage by ",
                seg.pct(damageRate), ' (via ', seg.blow(134), '/', seg.blow(135), ')',
            ];
            const limitRange = f.length > 2 ? ParamReader.num(f[2]) : NaN;
            if (!isNaN(limitRange) && limitRange >= 0) {
                out.push('; ends early if it drifts beyond ', seg.num(limitRange),
                    ' range of the target (dies when it ends)');
            } else {
                out.push('; unlimited range (dies when it ends)');
            }
            return out;
        },
    },
    249: {
        name: 'Pile Add Effect',
        wikiDesc: 'Applies a layered action when the skill is duplicated (piled). Parameter is the ' +
            'processor action name, e.g. Skill_SpecterOfPain_Explosion.',
        describe({ raw }) {
            const action = raw.trim();
            if (!action) {
                return null;
            }
            return ['When the skill is duplicated (piled), applies the layered action ', seg.action(action)];
        },
    },
    254: {
        name: 'Force Aggro Target',
        wikiDesc: 'Forces aggro on a target. If FindRandomTarget is set, pick a random target instead ' +
            'of using the current target.',
        describe({ params }) {
            const f = params.fields();
            const findRandom = ParamReader.num(f[0]);
            const onlyPlayers = ParamReader.num(f[1]);
            const amount = ParamReader.num(f[2]);
            const stateEffectId = ParamReader.num(f[3]);
            if (isNaN(findRandom) || isNaN(onlyPlayers) || isNaN(amount)) {
                return null;
            }
            const out: Segment[] = [
                'Forces ', findRandom === 1 ? 'a random' : 'the current', ' ',
                onlyPlayers === 1 ? 'player' : 'actor', ' as the aggro target, adjusting aggro by ',
                seg.num(amount),
            ];
            if (!isNaN(stateEffectId)) {
                out.push(', displaying ', seg.blow(stateEffectId));
            }
            return out;
        },
    },
    255: {
        name: 'EXP Modifier',
        wikiDesc: 'Adjusts EXP gained by Mod%. (Returned Hero Buff)',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            const dir = mod < 0 ? 'decreased' : 'increased';
            return [`EXP gained ${dir} by `, seg.pct(Math.abs(mod))];
        },
    },
    256: {
        name: 'Ally EXP Boost',
        wikiDesc: 'Increases ally EXP gained by Mod%.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ["Increases allies' EXP gained by ", seg.pct(mod)];
        },
    },
    259: {
        name: 'Durability Penalty Boost',
        wikiDesc: 'Decreases the durability penalty taken on death/becoming a ghost by Mod%.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['Decreases the death/ghost durability penalty by ', seg.pct(mod)];
        },
    },
    260: {
        name: 'Friendship Gift Boost',
        wikiDesc: 'Grants Mod% additional Friendship when giving gifts to NPCs.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['Grants ', seg.pct(mod), ' additional Friendship when gifting NPCs'];
        },
    },
    261: {
        name: 'Medal Boost',
        wikiDesc: 'Grants Param1 additional medals at the Duck Colosseum, up to Param2 medals per day ' +
            '(resets at midnight).',
        describe({ params }) {
            const f = params.numbers();
            const extra = f[0];
            const dailyCap = f[1];
            if (isNaN(extra) || isNaN(dailyCap)) {
                return null;
            }
            return [
                'Grants ', seg.num(extra), ' additional medals at the Duck Colosseum, up to ',
                seg.num(dailyCap), ' per day (resets at midnight)',
            ];
        },
    },
    262: {
        name: 'Farm Growth Boost',
        wikiDesc: 'Decreases farm crop growth time by 3% or 7%.',
        note: 'Parameter is a magic value: 268442041.1 = 3%, 268442043.1 = 7%.',
    },
    263: {
        name: 'Ladder Store Price Boost',
        wikiDesc: 'Grants a Mod% discount when purchasing from the Ladder Store.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['Grants a ', seg.pct(mod), ' discount at the Ladder Store'];
        },
    },
    265: {
        name: 'Cooking Mastery Bonus',
        wikiDesc: 'Increases the cooking mastery bonus granted when you successfully cook by Mod%.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['Increases the cooking mastery bonus on success by ', seg.pct(mod)];
        },
    },
    266: {
        name: 'Gold Boost',
        wikiDesc: 'Increases the gold gained from dungeons by Mod%.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['Increases gold gained from dungeons by ', seg.pct(mod)];
        },
    },
    270: {
        name: 'Drop Rate Modifier',
        wikiDesc: 'Adjusts item drop rate by Mod%.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            const dir = mod < 0 ? 'decreased' : 'increased';
            return [`Drop rate ${dir} by `, seg.pct(Math.abs(mod))];
        },
    },
    272: {
        name: 'HP Item Boost',
        wikiDesc: 'Increases the amount of HP restored by items by Mod%.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['Increases HP restored by items by ', seg.pct(mod)];
        },
    },
    274: {
        name: 'Damage Limit',
        wikiDesc: 'After taking DeltaHP% damage, uses SkillId.',
        describe({ params }) {
            const f = params.fields();
            const deltaHp = ParamReader.num(f[0]);
            const skillId = ParamReader.num(f[1]);
            if (isNaN(deltaHp) || isNaN(skillId)) {
                return null;
            }
            return ['After taking ', seg.pct(deltaHp), ' damage, uses ', seg.skill(skillId)];
        },
    },
    277: {
        name: 'Transform Monster',
        wikiDesc: "(Monster only) Replaces the monster's actor with ReplacementActorID and plays " +
            'StartActionName.',
        note: "Permanent — ending the effect doesn't restore the original actor; use another 277 to " +
            'revert.',
        describe({ params }) {
            const f = params.fields();
            const actorId = ParamReader.num(f[0]);
            const startAction = f[1];
            if (isNaN(actorId) || !startAction) {
                return null;
            }
            return [
                '(Monster only) Replaces the actor with ', seg.id(actorId), ', playing ',
                seg.action(startAction),
            ];
        },
    },
    279: {
        name: 'Party Bonus Damage',
        wikiDesc: 'Adds BonusDamageAmount flat bonus damage to each hit.',
        describe({ params }) {
            const amount = params.nextNum();
            if (isNaN(amount)) {
                return null;
            }
            return ['Adds ', seg.num(amount), ' flat bonus damage to each hit'];
        },
    },
    282: {
        name: 'Suffer Force Critical Damage',
        wikiDesc: 'The next Count hits this actor takes are forced criticals; removes itself when spent.',
        describe({ params }) {
            const count = params.nextNum();
            if (isNaN(count)) {
                return null;
            }
            return [
                'The next ', seg.num(count), ` hit${count === 1 ? '' : 's'}`,
                ' this actor takes are forced criticals (removes itself when spent)',
            ];
        },
    },
    287: {
        name: 'Monster Order',
        wikiDesc: "Forces the target monster's AI to cancel its current action/skill and use the AI " +
            "skill matching MonsterSkillID. Silently does nothing if the monster's AI doesn't have " +
            'that skill.',
        describe({ params }) {
            const skillId = params.nextNum();
            if (isNaN(skillId)) {
                return null;
            }
            return [
                "Forces the target monster's AI to cancel its current action and use ", seg.skill(skillId),
                " (no effect if the monster's AI doesn't have that skill)",
            ];
        },
    },
    292: {
        name: 'Add Weapon Base Damage',
        wikiDesc: "On hit, deals bonus damage equal to Ratio of your main weapon's max attack " +
            '(enchant included). One-shot per application.',
        describe({ params }) {
            const f = params.numbers();
            const attackType = f[0];
            const ratio = f[1];
            if (isNaN(attackType) || isNaN(ratio)) {
                return null;
            }
            return [
                'On hit, deals bonus ', seg.en(attackType, ['physical', 'magic'], 'unknown'),
                ' damage equal to ', seg.pct(ratio),
                " of your main weapon's max attack (one-shot per application)",
            ];
        },
    },
    293: {
        name: 'Add Damage by Target Distance',
        wikiDesc: 'When hitting a target at least Distance away, Probability% chance for bonus ' +
            'damage equal to Ratio of the damage dealt.',
        note: 'All 4 params are required — the old 3-param example (500;0.5;0) parses as all zeros.',
        describe({ params }) {
            const f = params.numbers();
            const distance = f[0];
            const ratio = f[1];
            const probability = f[2];
            const cooldownMs = f[3];
            if (isNaN(distance) || isNaN(ratio) || isNaN(probability) || isNaN(cooldownMs)) {
                return null;
            }
            const out: Segment[] = [
                'When hitting a target at least ', seg.num(distance), ' away, at a ',
                seg.pct(probability / 100), ' chance, deals bonus damage equal to ', seg.pct(ratio),
                ' of the damage dealt',
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    294: {
        name: 'Add Damage by Weight',
        wikiDesc: "On hit, if the target's weight exceeds Weight, or the hit caused no hit reaction " +
            '(stagger/knockback immune), bonus damage equal to Ratio of the damage dealt.',
        describe({ params }) {
            const f = params.numbers();
            const weight = f[0];
            const ratio = f[1];
            if (isNaN(weight) || isNaN(ratio)) {
                return null;
            }
            return [
                'On hit, if the target weighs over ', seg.num(weight),
                ' or the hit caused no hit reaction, deals bonus damage equal to ', seg.pct(ratio),
                ' of the damage dealt',
            ];
        },
    },
    298: {
        name: 'Transformation',
        wikiDesc: "Swaps the player's body skin and both weapon skins/anims to the given actor/" +
            'weapon table entries, hiding avatar parts; restored on end, with the end VFX playing ' +
            'about 1s before expiry.',
        describe({ params }) {
            const f = params.fields();
            const actorId = ParamReader.num(f[0]);
            const mainWeaponId = ParamReader.num(f[1]);
            const secondWeaponId = ParamReader.num(f[2]);
            const endEffectId = ParamReader.num(f[3]);
            if (isNaN(actorId) || isNaN(mainWeaponId) || isNaN(secondWeaponId) || isNaN(endEffectId)) {
                return null;
            }
            const out: Segment[] = [
                'Transforms into actor ', seg.id(actorId, params.span(0) || undefined),
                ' with main weapon ', seg.item(mainWeaponId, params.span(1) || undefined),
                ' and off weapon ', seg.item(secondWeaponId, params.span(2) || undefined),
                ', playing state VFX ', seg.stateVfx(endEffectId, params.span(3) || undefined),
                ' about 1s before expiry',
            ];
            const soundName = f[4];
            if (soundName) {
                out.push(', with sound ', seg.action(soundName, params.span(4) || undefined));
            }
            if (f[5] === '1') {
                out.push(' (only if the job skin option is enabled)');
            }
            return out;
        },
    },
    299: {
        name: 'Damage Transition to Summon Monster',
        wikiDesc: 'A Rate of every damage instance the owner takes is redirected to their summon of ' +
            'the given MonsterID instead. Tracks one summon; no effect while it is dead.',
        describe({ params }) {
            const f = params.numbers();
            const monsterId = f[0];
            const rate = f[1];
            if (isNaN(monsterId) || isNaN(rate)) {
                return null;
            }
            return [
                'Redirects ', seg.pct(rate), ' of incoming damage to the summoned ', seg.monster(monsterId),
                ' instead (tracks one summon; no effect while it is dead)',
            ];
        },
    },
    301: {
        name: 'DPS bar',
        wikiDesc: 'Displays a DPS bar (Type 0 = green/not usable, 1 = blue/HP, 2 = orange/SA, 3 = red/HP) ' +
            'with Durability HP. Uses SkillIDWhenBroken when broken. ShowUI 0 hides it from the screen, ' +
            "IgnoreSA (with an HP type) negates the incoming attack's SAB, UseHPWeight multiplies the " +
            "bar's HP by the monster's weight table entry (does not work in labyrinth), and AddMaxHPRatio " +
            "adds a percent of the monster's max HP as durability.",
        note: 'AddMaxHPRatio is a 0–1 ratio, not a percentage.',
        describe({ params }) {
            const f = params.numbers();
            const type = f[0];
            const durability = f[1];
            if (isNaN(type) || isNaN(durability)) {
                return null;
            }
            const TYPES = ['green (not usable)', 'blue (HP)', 'orange (SA)', 'red (HP)'];
            const out: Segment[] = [
                'Displays a ', seg.en(type, TYPES, 'unknown', params.span(0) || undefined),
                ' DPS bar with ', seg.num(durability, params.span(1) || undefined),
                ' durability',
            ];
            const skillIdWhenBroken = f[2];
            if (!isNaN(skillIdWhenBroken) && skillIdWhenBroken > 0) {
                out.push(', using ', seg.skill(skillIdWhenBroken, params.span(2) || undefined), ' when broken');
            }
            if (f[3] === 0) {
                out.push(' (hidden from screen)');
            }
            if (f[4] === 1) {
                out.push(", ignoring the triggering attack's super armor break");
            }
            if (f[5] === 1) {
                out.push(", scaled by the monster's weight table entry (not in labyrinth)");
            }
            if (f[6]) {
                out.push(', plus ', seg.pct(f[6], 2, params.span(6) || undefined),
                    " of the monster's max HP as durability");
            }
            if (f[7] === 0) {
                out.push(' (timer hidden)');
            }
            return out;
        },
    },
    302: {
        name: 'Delivery HP',
        wikiDesc: 'Every Interval ms, drains HPAmount from the holder and adds it to the first living ' +
            'monster found in the given spawn (birth) area; transfers once more when it ends. 70lv DN ' +
            'prop-clicking gimmick.',
        describe({ params }) {
            const f = params.numbers();
            const intervalMs = f[0];
            const areaHandle = f[1];
            const hpAmount = f[2];
            if (isNaN(intervalMs) || isNaN(areaHandle) || isNaN(hpAmount)) {
                return null;
            }
            return [
                'Every ', seg.secs(intervalMs), ', drains ', seg.num(hpAmount),
                ' HP from the holder and adds it to the first living monster in spawn area ',
                seg.id(areaHandle), ' (transfers once more when it ends)',
            ];
        },
    },
    314: {
        name: 'Decrease Super Armor',
        wikiDesc: "A chance to decrease a hit target's super armor by DecreaseRatio; 1.0 completely " +
            "ignores the target's super armor. Can break targets with infinite super armor (blow 156).",
        note: "The HITTER actor needs this state effect applied to itself for it to break TARGET actors' SA.",
        describe({ params }) {
            const f = params.numbers();
            const decreaseRatio = f[0];
            const chance = f[1];
            if (isNaN(decreaseRatio) || isNaN(chance)) {
                return null;
            }
            return [
                'At a ', seg.pct(chance), " chance, decreases a hit target's super armor by ",
                seg.pct(decreaseRatio), ' (can break through ', seg.blow(156), ')',
            ];
        },
    },
    319: {
        name: 'Duplicate Explosion and Pass By Disconnect',
        wikiDesc: '(BDN Golem Ice Ball) If the player with this effect disconnects, it transfers to ' +
            'another player. If this effect is duplicated on a target, it explodes for the given damage.',
        describe({ params }) {
            const damage = params.nextNum();
            if (isNaN(damage)) {
                return null;
            }
            return [
                'If duplicated, explodes for ', seg.num(damage),
                ' damage; if the holder disconnects, transfers to another player',
            ];
        },
    },
    324: {
        name: 'Stamina to MP Attack',
        wikiDesc: 'Adds Value flat attack per point of VIT. Client-side stat display only — the server ' +
            'never reads it, so treat it as cosmetic.',
        describe({ params }) {
            const f = params.numbers();
            const value = f[0];
            if (isNaN(value)) {
                return null;
            }
            const out: Segment[] = ['Adds ', seg.num(value), ' flat attack per point of VIT'];
            const attackType = f[1];
            if (!isNaN(attackType)) {
                out.push(' (', seg.en(attackType, ['magic', 'physical'], 'unknown'), ' type)');
            }
            out.push(' (cosmetic client display only)');
            return out;
        },
    },
    326: {
        name: 'Cannot gain aggro',
        wikiDesc: 'Prevents the holder from generating aggro.',
        describe() {
            return ['Prevents the holder from generating aggro'];
        },
    },
    348: {
        name: 'Be ignored by ACT hit block target type All',
        wikiDesc: 'Makes the holder excluded from ACT hit blocks whose target type is set to "All".',
        describe() {
            return ['Excluded from ACT hit blocks whose target type is set to "All"'];
        },
    },
    349: {
        name: 'Passive bubble generation',
        wikiDesc: 'Every IntervalMillis ms, gain 1x BubbleID.',
        describe({ params }) {
            const f = params.numbers();
            const intervalMs = f[0];
            const bubbleId = f[1];
            if (isNaN(intervalMs) || isNaN(bubbleId)) {
                return null;
            }
            return ['Every ', seg.secs(intervalMs), ', gain 1x bubble ', seg.id(bubbleId)];
        },
    },
    350: {
        name: 'Passive bubble consumption',
        wikiDesc: 'Every IntervalMillis ms, consume 1x BubbleID.',
        describe({ params }) {
            const f = params.numbers();
            const intervalMs = f[0];
            const bubbleId = f[1];
            if (isNaN(intervalMs) || isNaN(bubbleId)) {
                return null;
            }
            return [
                'Every ', seg.secs(intervalMs, params.span(0) || undefined), ', consume 1x bubble ',
                seg.id(bubbleId, params.span(1) || undefined),
            ];
        },
    },
    352: {
        name: 'Command Mercenary to use Active Skill',
        wikiDesc: 'Commands the mercenary to use one of its two available active skills (1 = level 10 ' +
            'active skill, 2 = level 20).',
        describe({ params }) {
            const slot = params.nextNum();
            if (isNaN(slot)) {
                return null;
            }
            const TYPES = ['(invalid)', 'Lvl 10 active skill', 'Lvl 20 active skill'];
            return ['Commands the mercenary to use its ', seg.en(slot, TYPES, 'unknown')];
        },
    },
    353: {
        name: 'Hot spring buff',
        wikiDesc: "Hot spring manager: fills a gauge 1 point/sec (boosted by the day's spa costume and " +
            "spa girl time windows) and grants the day's reward (party buff item or fatigue points) at 3 " +
            'gauge thresholds.',
        note: 'No parameters.',
    },
    355: {
        name: 'Hit Effect Change',
        wikiDesc: 'Replaces normal-attack hit particles/sounds with the set in row TableID of ' +
            'attackfxchangetable (client cosmetic). Used by Force Tuners.',
        describe({ params }) {
            const tableId = params.nextNum();
            if (isNaN(tableId)) {
                return null;
            }
            return [
                'Replaces normal-attack hit particles/sounds with row ', seg.id(tableId),
                ' of attackfxchangetable (client cosmetic)',
            ];
        },
    },
    360: {
        name: 'Change All Defense Absolute / Force DEF to 0',
        wikiDesc: "Forces the target's PDEF/MDEF and all four elemental resists to 0 at the end of stat " +
            'calc, overriding everything (the "absolute value" is hardcoded 0; params are ignored). Red ' +
            'Dragon Nest.',
        describe() {
            return [
                "Forces the target's PDEF/MDEF and all four elemental resists to 0 at the end of stat " +
                'calc, overriding everything',
            ];
        },
    },
    361: {
        name: 'Charger Renew',
        wikiDesc: "Replays every signal of the caster action ActionName at the victim's position each " +
            "frame, for Count frames (an improved version of blow 62's charger).",
        note: 'Abandoned mid-development — avoid using.',
        describe({ params }) {
            const count = params.nextNum();
            const action = params.rest();
            if (isNaN(count) || !action) {
                return null;
            }
            return [
                'Replays every signal of ', seg.action(action), " at the victim's position each frame, " +
                'for ', seg.num(count), ' frames',
            ];
        },
    },
    362: {
        name: 'Apply Aura Skill for Duration',
        wikiDesc: "Forces the holder's aura-type skill SkillID to manifest (switching off any active " +
            "aura first); switched off again when this ends. Does nothing if the holder doesn't know " +
            'the skill or it is not an aura skill. No buff icon.',
        describe({ params }) {
            const skillId = params.nextNum();
            if (isNaN(skillId)) {
                return null;
            }
            return [
                'Forces the aura skill ', seg.skill(skillId),
                ' to manifest, switching off any other active aura first (no effect if the holder ' +
                "doesn't know the skill or it isn't an aura; no buff icon)",
            ];
        },
    },
    369: {
        name: 'Fade',
        wikiDesc: 'Used by Fade. Applies attachedStateEffectId (typically 73, invisibility) — created ' +
            'from attachedSkillId if needed — for its own duration.',
        describe({ params }) {
            const f = params.numbers();
            const fadeDurationMs = f[0];
            const attachedStateEffectId = f[1];
            const attachedSkillId = f[2];
            if (isNaN(fadeDurationMs) || isNaN(attachedStateEffectId) || isNaN(attachedSkillId)) {
                return null;
            }
            return [
                'Applies ', seg.blow(attachedStateEffectId), ' (from ', seg.skill(attachedSkillId),
                ') for ', seg.secs(fadeDurationMs),
            ];
        },
    },
    370: {
        name: 'Remove Buff When Bubble Count Drops to Value',
        wikiDesc: 'Removes the effects granted by the listed skill IDs when BubbleID drops to Count ' +
            '(or below, if IncludeLess is set).',
        describe({ params }) {
            const f = params.fields();
            const bubbleId = ParamReader.num(f[0]);
            const count = ParamReader.num(f[1]);
            const skillIdFieldSpan = params.span(2);
            const skillIdsReader = skillIdFieldSpan ? new ParamReader(f[2], skillIdFieldSpan[0]) : null;
            const skillIdTokens = f.length > 2 ? ParamReader.list(f[2], ',') : [];
            const items = sharedSpanListItems(skillIdTokens, skillIdsReader, ',', seg.skill);
            if (isNaN(bubbleId) || isNaN(count) || !items.length) {
                return null;
            }
            const includeLess = f[3] === '1';
            const isSkillAutoLearned = f[4] === '1';
            const out: Segment[] = [
                'Removes the effects granted by ',
                seg.list(items),
                ' when bubble ', seg.id(bubbleId, params.span(0) || undefined),
                includeLess ? ' drops to or below ' : ' drops to ',
                seg.num(count, params.span(1) || undefined),
            ];
            if (isSkillAutoLearned) {
                out.push(' (treats the skills as auto-learned)');
            }
            return out;
        },
    },
    371: {
        name: 'Big Head',
        wikiDesc: "Scales the player's head to Ratio% for the duration (300 = 3x head size). Client " +
            'visual; used as a monster skill gimmick.',
        describe({ params }) {
            const ratio = params.nextNum();
            if (isNaN(ratio)) {
                return null;
            }
            return ["Scales the player's head to ", seg.pct(ratio / 100), ' of normal size'];
        },
    },
    372: {
        name: 'Increase Stat by Percent of Another Stat',
        wikiDesc: 'Convert Rate% of one stat to another stat, for one or more X→Y pairs. If ' +
            'RecheckIntervalMs is omitted, the conversion is computed once on apply; otherwise it ' +
            'recomputes every RecheckIntervalMs.',
        describe({ raw }) {
            const bar = raw.lastIndexOf('|');
            const body = bar >= 0 ? raw.slice(0, bar) : raw;
            const bodyReader = new ParamReader(body);
            const f = body.split(';').map((s) => s.trim());
            const items: Segment[][] = [];
            for (let i = 0; i + 2 < f.length; i += 3) {
                const x = ParamReader.num(f[i]);
                const rate = ParamReader.num(f[i + 1]);
                const y = ParamReader.num(f[i + 2]);
                if (isNaN(x) || isNaN(rate) || isNaN(y)) {
                    continue;
                }
                items.push([
                    seg.pct(rate, 2, bodyReader.span(i + 1) || undefined), ' of ',
                    seg.en(x, STATS_372, 'unknown', bodyReader.span(i) || undefined), ' to ',
                    seg.en(y, STATS_372, 'unknown', bodyReader.span(i + 2) || undefined),
                ]);
            }
            if (!items.length) {
                return null;
            }
            const out: Segment[] = ['Converts '];
            items.forEach((item, i) => {
                if (i > 0) {
                    out.push('; ');
                }
                out.push(...item);
            });
            if (bar >= 0) {
                const recheckMs = ParamReader.num(raw.slice(bar + 1));
                if (!isNaN(recheckMs) && recheckMs > 0) {
                    const recheckSpan = new ParamReader(raw.slice(bar + 1), bar + 1).span(0) || undefined;
                    out.push(' (recomputed every ', seg.secs(recheckMs, recheckSpan), ')');
                } else {
                    out.push(' (computed once on apply)');
                }
            } else {
                out.push(' (computed once on apply)');
            }
            return out;
        },
    },
    373: {
        name: 'Additional Damage on Resists',
        wikiDesc: 'Deals Mod% additional damage when the target resists StateEffectID.',
        describe({ params }) {
            const f = params.numbers();
            const stateEffectId = f[0];
            const mod = f[1];
            if (isNaN(stateEffectId) || isNaN(mod)) {
                return null;
            }
            return [
                'Deals ', seg.pct(mod), ' additional damage when the target resists ',
                seg.blow(stateEffectId),
            ];
        },
    },
    376: {
        name: 'Apply Visual Effect',
        wikiDesc: 'Cosmetic only. Applies a state-effect-table VFX by ID, or plays the given action ' +
            'name as the given effect type.',
        describe({ raw }) {
            const trimmed = raw.trim();
            if (trimmed === '') {
                return null;
            }
            if (trimmed.indexOf(';') === -1) {
                const id = Number(trimmed);
                if (isNaN(id)) {
                    return null;
                }
                return ['Applies VFX ', seg.stateVfx(id)];
            }
            const parts = trimmed.split(';');
            const type = ParamReader.num(parts[0]);
            const actionName = parts.slice(1).join(';');
            if (isNaN(type) || !actionName) {
                return null;
            }
            return [
                'Plays ', seg.action(actionName), ' from PlayerCommonEffect.act as effect type ',
                seg.id(type),
            ];
        },
    },
    378: {
        name: 'Use Skill Add Max MP Ratio',
        wikiDesc: 'Using any of the listed skills restores maxMPRatio × max MP (negative drains). ' +
            'Crest effect.',
        describe({ params }) {
            const f = params.fields();
            const ratio = ParamReader.num(f[0]);
            const skillIds = f.length > 1 ? ParamReader.numList(f[1], ',') : [];
            if (isNaN(ratio) || !skillIds.length) {
                return null;
            }
            const verb = ratio < 0 ? 'drains' : 'restores';
            return [
                'Using ', seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])), ` ${verb} `,
                seg.pct(Math.abs(ratio)), ' of max MP',
            ];
        },
    },
    379: {
        name: 'Add Hero EXP',
        wikiDesc: 'Hero EXP from stage clears is increased by Value (0.1 = +10%); instances sum.',
        describe({ params }) {
            const value = params.nextNum();
            if (isNaN(value)) {
                return null;
            }
            return ['Hero EXP from stage clears increased by ', seg.pct(value), ' (instances sum)'];
        },
    },
    380: {
        name: 'Hero Level',
        wikiDesc: 'Applies the herolevel table stat bonuses as if the hero level were HeroLevel; ' +
            "instances sum their values. No stats if the resulting level isn't in the table.",
        describe({ params }) {
            const level = params.nextNum();
            if (isNaN(level)) {
                return null;
            }
            return [
                'Applies stat bonuses as if the hero level were ', seg.num(level),
                " (instances sum; no effect if the level isn't in the herolevel table)",
            ];
        },
    },
    381: {
        name: 'Change Just Stand Action',
        wikiDesc: 'Replaces only the Stand action with the literal "Stand"+Suffix (unlike blow 121, ' +
            'which swaps a whole action set), playing EndActionName when it ends. Only one instance ' +
            'may be active.',
        note: 'The `;` before EndActionName is required even when EndActionName is empty.',
        describe({ params }) {
            const suffix = params.next();
            const endAction = params.rest();
            if (!suffix) {
                return null;
            }
            const out: Segment[] = ['Replaces Stand with the action ', seg.action('Stand' + suffix)];
            if (endAction) {
                out.push(', playing ', seg.action(endAction), ' when it ends');
            }
            out.push(' (only one instance may be active)');
            return out;
        },
    },
    383: {
        name: 'Transform Monster',
        wikiDesc: 'Transforms the actor into monster ActorTableID, showing state-effect VFX ' +
            'stateEffectIdToShow.',
        describe({ params }) {
            const f = params.numbers();
            const actorId = f[0];
            const stateEffectId = f[1];
            if (isNaN(actorId) || isNaN(stateEffectId)) {
                return null;
            }
            return [
                'Transforms into ', seg.monster(actorId), ', showing state-effect VFX ',
                seg.id(stateEffectId),
            ];
        },
    },
    384: {
        name: 'Change Skill Damage Scaling',
        wikiDesc: "Changes the current skill's damage scaling to that of skillId.",
        describe({ params }) {
            const skillId = params.nextNum();
            if (isNaN(skillId)) {
                return null;
            }
            return ["Changes this skill's damage scaling to that of ", seg.skill(skillId)];
        },
    },
    385: {
        name: 'WASD Skill Selection',
        wikiDesc: 'Pressing a directional key (W/A/S/D) uses a specific skill instead, with the WASD ' +
            'Skill Selection UI shown. -1 for a key means no skill is bound.',
        describe({ params }) {
            const f = params.numbers();
            if (f.length < 4) {
                return null;
            }
            const KEYS = ['W', 'A', 'S', 'D'];
            const items: Segment[][] = [];
            for (let i = 0; i < 4; ++i) {
                const skillId = f[i];
                items.push([
                    `${KEYS[i]}: `,
                    (!isNaN(skillId) && skillId >= 0) ? seg.skill(skillId) : seg.val('(none)'),
                ]);
            }
            return ['Directional key skill bindings: ', seg.list(items)];
        },
    },
    387: {
        name: 'Lock Zoom In/Out',
        wikiDesc: 'Locks the camera zoom at Distance with a radial blur transition, disabling zoom ' +
            'input (client, local player only). Effect duration must be at least 2x the transition ' +
            'time or it cancels itself; do not set Distance below 100.',
        describe({ params }) {
            const f = params.numbers();
            if (f.length < 7 || f.slice(0, 7).some(isNaN)) {
                return null;
            }
            const distance = f[0];
            const transitionSec = f[1];
            const centerX = f[2];
            const centerY = f[3];
            const blurSize = f[4];
            const beginRatio = f[5];
            const endRatio = f[6];
            return [
                'Locks the camera zoom at ', seg.num(distance), ' distance over a ',
                seg.secs(transitionSec * 1000), ' radial-blur transition (center ', seg.num(centerX),
                ', ', seg.num(centerY), '; blur size ', seg.num(blurSize), '; ', seg.pct(beginRatio),
                ' → ', seg.pct(endRatio), '), disabling zoom input (client, local player only)',
            ];
        },
    },
    388: {
        name: 'Additional Hero EXP Gained',
        wikiDesc: 'Grants Rate% additional hero EXP on stage clear (capped by the global extra hero ' +
            'exp limit); instances sum.',
        describe({ params }) {
            const rate = params.nextNum();
            if (isNaN(rate)) {
                return null;
            }
            return [
                'Grants ', seg.pct(rate),
                ' additional hero EXP on stage clear (capped by the global limit; instances sum)',
            ];
        },
    },
    390: {
        name: 'Unified Elemental Attack',
        wikiDesc: 'Among the listed elements, takes the highest elemental attack % and sets every ' +
            'listed element to it (restored when this ends). A lone 0 means all four elements.',
        describe({ params }) {
            const ids = params.numbers().filter((v) => !isNaN(v));
            if (!ids.length) {
                return null;
            }
            if (ids.length === 1 && ids[0] === 0) {
                return ['Unifies fire/ice/light/dark attack % to the highest of the four'];
            }
            return [
                'Unifies ',
                seg.list(ids.map((id) => [seg.en(id, ELEMENTS_1_BASED_ALL, 'unknown')] as Segment[])),
                ' attack % to the highest among them',
            ];
        },
    },
    391: {
        name: 'Ignore Bubble Requirement',
        wikiDesc: 'The listed skills ignore the minimum-bubble-count requirement usable checker.',
        describe({ params }) {
            const g = params.groups();
            if (g.groups.length < 3) {
                return null;
            }
            const bubbleIds = ParamReader.numList(g.groups[0], ';');
            const count = ParamReader.num(g.groups[1]);
            const skillIds = ParamReader.numList(g.groups[2], ';');
            if (!bubbleIds.length || isNaN(count) || !skillIds.length) {
                return null;
            }
            return [
                seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])),
                ' ignore the minimum bubble count (', seg.num(count), ') requirement for bubbles ',
                seg.list(bubbleIds.map((id) => [seg.id(id)] as Segment[])),
            ];
        },
    },
    403: {
        name: 'Reflect Skill',
        wikiDesc: "On being hit, reflects the attacker's skill effects back at them.",
        describe({ params }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const checkCanBeHit = ParamReader.num(f[0]);
            const skillIds = f.slice(1).map(ParamReader.num).filter((v) => !isNaN(v));
            if (isNaN(checkCanBeHit) || !skillIds.length) {
                return null;
            }
            const out: Segment[] = [
                'Reflects ', seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])),
                "'s effects back at the attacker on being hit",
            ];
            if (checkCanBeHit === 1) {
                out.push(' (only if the holder can be hit)');
            }
            return out;
        },
    },
    406: {
        name: 'Apply Elemental Type',
        wikiDesc: 'Forces a skill with no initial elemental type to use the given elemental type. ' +
            'Cannot force back to no elemental type via NULL/-1/0.',
        note: '1-based enum (1 Fire, 2 Ice, 3 Light, 4 Dark) — unlike blow 182, which is 0-based.',
        describe({ params }) {
            const element = params.nextNum();
            if (isNaN(element) || element <= 0) {
                return [
                    'No effect — the game cannot force a non-elemental type (values -1/0 are ignored)',
                ];
            }
            return [
                'Sets skills with no innate element to ',
                seg.en(element, ELEMENTS_1_BASED_NONE, 'unknown'), ' element',
            ];
        },
    },
    407: {
        name: 'Overlay',
        wikiDesc: 'Applies a screen overlay texture while active. Type 0 = none, 1 = blink.',
        describe({ params }) {
            const f = params.numbers();
            const type = f[0];
            if (isNaN(type)) {
                return null;
            }
            const TYPES = ['none', 'blink'];
            const out: Segment[] = ['Screen overlay: ', seg.en(type, TYPES, 'unknown')];
            const textureId = f[1];
            if (!isNaN(textureId)) {
                out.push(' using texture ', seg.id(textureId));
            }
            const r = f[2];
            const g = f[3];
            const b = f[4];
            const a = f[5];
            if (![r, g, b, a].some(isNaN)) {
                out.push(' (RGBA ', seg.num(r), ', ', seg.num(g), ', ', seg.num(b), ', ', seg.num(a), ')');
            }
            return out;
        },
    },
    408: {
        name: 'Custom Condition Blow',
        wikiDesc: 'Applies its effects when a condition from CustomConditionTable row ' +
            'CustomConditionTable is satisfied (hit, HP threshold, skill use, attacking, blow applied, ' +
            'idle timers, etc).',
        describe({ params }) {
            const id = params.nextNum();
            if (isNaN(id)) {
                return null;
            }
            return [
                'Applies its effects when the condition in CustomConditionTable row ', seg.id(id),
                ' is satisfied',
            ];
        },
    },
    410: {
        name: 'Dummy Projectile Trigger',
        wikiDesc: 'Dummy blow with no effect of its own; following projectiles/signals BlowCheck for ' +
            'it to trigger. Also blocks input-driven action changes on the local player while applied.',
        describe() {
            return [
                'No effect of its own; following projectiles/signals BlowCheck for it to trigger. ' +
                'Also blocks input-driven action changes on the local player while applied',
            ];
        },
    },
};
